import React, { useState, useEffect, useCallback, useMemo, memo } from 'react';
import { Link } from 'react-router-dom';
import healthService from '../../services/healthService';
import authService from '../../services/authService';
import { useHealthCheck } from '../../hooks/useHealthCheck';
import { handleApiError } from '../../utils/errorHandler';
import { formatTime, formatDateTime } from '../../utils/dateFormatter';
import './Dashboard.css';

const Dashboard = memo(() => {
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { health, isHealthy } = useHealthCheck();
  const user = authService.getUser();

  const fetchMetrics = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const data = await healthService.getDashboardStats();
      setMetrics(data);
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMetrics();
  }, [fetchMetrics]);

  const getGreeting = useMemo(() => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good Morning';
    if (hour < 18) return 'Good Afternoon';
    return 'Good Evening';
  }, []);

  return (
    <div className="container-padding section-spacing">
      {/* Header with Gradient */}
      <div className="mb-12 relative">
        <div className="relative z-10">
          <h1 className="text-5xl font-extrabold bg-gradient-to-r from-gray-900 via-red-700 to-gray-900 bg-clip-text text-transparent">
            {getGreeting}, {user?.name}!
          </h1>
          <p className="mt-3 text-lg text-gray-600 font-medium">Welcome back to your dashboard</p>
        </div>
        {/* Decorative line */}
        <div className="mt-4 h-1 w-32 bg-gradient-to-r from-red-600 to-red-400 rounded-full"></div>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="rounded-2xl bg-gradient-to-r from-red-50 to-rose-50 border-2 border-red-200 p-6 mb-10 shadow-lg animate-shake">
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0">
              <div className="w-10 h-10 rounded-lg bg-red-500 flex items-center justify-center">
                <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
            <div className="flex-1">
              <h3 className="text-sm font-bold text-red-900 mb-1">Error Loading Dashboard</h3>
              <p className="text-sm text-red-700">{error}</p>
              <button
                onClick={fetchMetrics}
                className="mt-3 px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-medium rounded-lg transition-colors duration-200 shadow-md"
              >
                Retry
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Loading State */}
      {loading && !metrics ? (
        <div className="card-base text-center py-20 shadow-lg rounded-2xl">
          <div className="inline-block animate-spin rounded-full h-16 w-16 border-4 border-gray-200 border-t-red-600 shadow-lg"></div>
          <p className="mt-6 text-lg font-medium text-gray-700">Loading dashboard metrics...</p>
          <p className="mt-2 text-sm text-gray-500">Please wait while we fetch your data</p>
        </div>
      ) : metrics ? (
        <>
          {/* Key Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
            {/* Vehicles */}
            <Link to="/vehicles" className="group">
              <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-red-500 via-red-600 to-red-700 p-6 shadow-lg hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300">
                <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-16 -mt-16"></div>
                <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/10 rounded-full -ml-12 -mb-12"></div>
                <div className="relative z-10">
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center shadow-lg">
                      <svg className="w-8 h-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                    </div>
                  </div>
                  <h3 className="text-sm font-semibold text-red-100 uppercase tracking-wide">Total Vehicles</h3>
                  <p className="text-4xl font-extrabold text-white mt-2">
                    {metrics.metrics?.vehicles?.total || 0}
                  </p>
                  <p className="text-sm text-red-100 mt-2 font-medium">
                    {metrics.metrics?.vehicles?.active || 0} active
                  </p>
                  <div className="mt-4 flex items-center text-sm text-white font-medium group-hover:translate-x-1 transition-transform duration-200">
                    View Details
                    <svg className="w-4 h-4 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </div>
              </div>
            </Link>

            {/* Tyres */}
            <Link to="/tyres" className="group">
              <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-emerald-500 via-green-600 to-teal-600 p-6 shadow-lg hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300">
                <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-16 -mt-16"></div>
                <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/10 rounded-full -ml-12 -mb-12"></div>
                <div className="relative z-10">
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center shadow-lg">
                      <svg className="w-8 h-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 10h.01M15 10h.01M9.5 15.5a3.5 3.5 0 005 0" />
                      </svg>
                    </div>
                  </div>
                  <h3 className="text-sm font-semibold text-green-100 uppercase tracking-wide">Total Tyres</h3>
                  <p className="text-4xl font-extrabold text-white mt-2">
                    {metrics.metrics?.tyres?.total || 0}
                  </p>
                  {metrics.metrics?.tyres?.by_status && (
                    <p className="text-sm text-green-100 mt-2 font-medium">
                      {metrics.metrics.tyres.by_status.assigned || 0} assigned
                    </p>
                  )}
                  <div className="mt-4 flex items-center text-sm text-white font-medium group-hover:translate-x-1 transition-transform duration-200">
                    View Details
                    <svg className="w-4 h-4 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </div>
              </div>
            </Link>

            {/* Trips */}
            <Link to="/trips" className="group">
              <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-blue-500 via-indigo-600 to-purple-600 p-6 shadow-lg hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300">
                <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-16 -mt-16"></div>
                <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/10 rounded-full -ml-12 -mb-12"></div>
                <div className="relative z-10">
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center shadow-lg">
                      <svg className="w-8 h-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                      </svg>
                    </div>
                  </div>
                  <h3 className="text-sm font-semibold text-blue-100 uppercase tracking-wide">Total Trips</h3>
                  <p className="text-4xl font-extrabold text-white mt-2">
                    {metrics.metrics?.trips?.total || 0}
                  </p>
                  <p className="text-sm text-blue-100 mt-2 font-medium">
                    {metrics.metrics?.trips?.this_week || 0} this week
                  </p>
                  <div className="mt-4 flex items-center text-sm text-white font-medium group-hover:translate-x-1 transition-transform duration-200">
                    View Details
                    <svg className="w-4 h-4 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </div>
              </div>
            </Link>

            {/* Resoles */}
            <Link to="/resoles" className="group">
              <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-amber-500 via-orange-600 to-red-600 p-6 shadow-lg hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300">
                <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-16 -mt-16"></div>
                <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/10 rounded-full -ml-12 -mb-12"></div>
                <div className="relative z-10">
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center shadow-lg">
                      <svg className="w-8 h-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    </div>
                  </div>
                  <h3 className="text-sm font-semibold text-orange-100 uppercase tracking-wide">Resole Operations</h3>
                  <p className="text-4xl font-extrabold text-white mt-2">
                    {metrics.metrics?.resoles?.total || 0}
                  </p>
                  <p className="text-sm text-orange-100 mt-2 font-medium">
                    {metrics.metrics?.resoles?.pending || 0} pending
                  </p>
                  <div className="mt-4 flex items-center text-sm text-white font-medium group-hover:translate-x-1 transition-transform duration-200">
                    View Details
                    <svg className="w-4 h-4 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </div>
              </div>
            </Link>
          </div>

          {/* Tyre Status Breakdown */}
          {metrics.metrics?.tyres?.by_status && (
            <div className="bg-white rounded-2xl shadow-lg p-8 mb-10 border border-gray-100">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Tyre Status Breakdown</h2>
                  <p className="text-sm text-gray-600 mt-1">Overview of all tyre statuses</p>
                </div>
                <div className="w-12 h-12 bg-gradient-to-br from-red-500 to-red-600 rounded-xl flex items-center justify-center shadow-lg">
                  <svg className="w-7 h-7 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                {Object.entries(metrics.metrics.tyres.by_status).map(([status, count]) => (
                  <div key={status} className="relative group">
                    <div className="text-center p-5 bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl shadow-md hover:shadow-lg transform hover:-translate-y-1 transition-all duration-300 border border-gray-200">
                      <p className="text-3xl font-extrabold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">{count}</p>
                      <p className="text-xs text-gray-600 mt-2 font-semibold uppercase tracking-wide">
                        {status.replace('_', ' ')}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Quick Actions */}
          <div className="bg-white rounded-2xl shadow-lg p-8 border border-gray-100">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Quick Actions</h2>
                <p className="text-sm text-gray-600 mt-1">Frequently used operations</p>
              </div>
              <div className="w-12 h-12 bg-gradient-to-br from-red-500 to-red-600 rounded-xl flex items-center justify-center shadow-lg">
                <svg className="w-7 h-7 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {authService.isAdmin() && (
                <>
                  <Link
                    to="/vehicles/create"
                    className="group flex items-center justify-center px-6 py-4 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white font-semibold rounded-xl shadow-md hover:shadow-lg transform hover:-translate-y-0.5 transition-all duration-200"
                  >
                    <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    Add Vehicle
                  </Link>
                  <Link
                    to="/tyres/create"
                    className="group flex items-center justify-center px-6 py-4 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white font-semibold rounded-xl shadow-md hover:shadow-lg transform hover:-translate-y-0.5 transition-all duration-200"
                  >
                    <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    Add Tyre
                  </Link>
                  <Link
                    to="/register"
                    className="group flex items-center justify-center px-6 py-4 bg-gradient-to-r from-gray-700 to-gray-800 hover:from-gray-800 hover:to-gray-900 text-white font-semibold rounded-xl shadow-md hover:shadow-lg transform hover:-translate-y-0.5 transition-all duration-200"
                  >
                    <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                    </svg>
                    Register User
                  </Link>
                </>
              )}
              {authService.isWorkerOrHigher() && (
                <Link
                  to="/trips/create"
                  className="group flex items-center justify-center px-6 py-4 bg-gradient-to-r from-gray-700 to-gray-800 hover:from-gray-800 hover:to-gray-900 text-white font-semibold rounded-xl shadow-md hover:shadow-lg transform hover:-translate-y-0.5 transition-all duration-200"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Record Trip
                </Link>
              )}
            </div>
          </div>

          {/* System Info */}
          <div className="mt-8 bg-gradient-to-r from-gray-50 to-gray-100 rounded-2xl p-6 border border-gray-200 shadow-sm">
            <div className="flex items-center justify-center space-x-8 text-sm">
              <div className="flex items-center space-x-2">
                <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <p className="text-xs text-gray-500 font-medium">System Uptime</p>
                  <p className="text-sm font-bold text-gray-900">{metrics.uptime_seconds ? Math.floor(metrics.uptime_seconds / 3600) : 0} hours</p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                </div>
                <div>
                  <p className="text-xs text-gray-500 font-medium">Last Updated</p>
                  <p className="text-sm font-bold text-gray-900">{formatDateTime(metrics.timestamp)}</p>
                </div>
              </div>
            </div>
          </div>

          {/* System Health Status */}
          <div className="mt-8">
            <div
              className={`relative overflow-hidden rounded-2xl shadow-lg ${
                isHealthy
                  ? 'bg-gradient-to-r from-green-50 via-emerald-50 to-green-50 border-2 border-green-200'
                  : 'bg-gradient-to-r from-red-50 via-rose-50 to-red-50 border-2 border-red-200'
              } p-6 transition-all duration-300 hover:shadow-xl`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className={`w-14 h-14 rounded-xl ${
                    isHealthy ? 'bg-green-500' : 'bg-red-500'
                  } flex items-center justify-center shadow-lg`}>
                    {isHealthy ? (
                      <svg className="w-8 h-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    ) : (
                      <svg className="w-8 h-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    )}
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">
                      System Status: <span className={isHealthy ? 'text-green-600' : 'text-red-600'}>
                        {isHealthy ? 'Healthy' : 'Unhealthy'}
                      </span>
                    </h3>
                    {health.timestamp && (
                      <p className="text-sm text-gray-600 mt-1">
                        Last checked: {formatTime(health.timestamp)}
                      </p>
                    )}
                  </div>
                </div>
                <div className={`w-4 h-4 rounded-full ${
                  isHealthy ? 'bg-green-500' : 'bg-red-500'
                } animate-pulse shadow-lg`}></div>
              </div>
              {health.checks && Object.keys(health.checks).length > 0 && (
                <div className="mt-5 flex flex-wrap gap-4">
                  {Object.entries(health.checks).map(([key, value]) => {
                    const displayValue = typeof value === 'object' && value !== null
                      ? value.status || JSON.stringify(value)
                      : value;
                    const isCheckHealthy = displayValue === 'ok' || displayValue === 'healthy';

                    return (
                      <div key={key} className="flex items-center space-x-2 bg-white/60 backdrop-blur-sm px-4 py-2 rounded-lg shadow-sm">
                        <div className={`w-2 h-2 rounded-full ${isCheckHealthy ? 'bg-green-500' : 'bg-red-500'}`}></div>
                        <span className="text-sm font-semibold text-gray-700 capitalize">{key}:</span>
                        <span className={`text-sm font-medium ${isCheckHealthy ? 'text-green-600' : 'text-red-600'}`}>
                          {displayValue}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
});

Dashboard.displayName = 'Dashboard';

export default Dashboard;
